import {useEffect, useState} from 'react';

const HIDDEN_CONTENT_TOLERANCE_PX = 1;

/**
 * Whether the given horizontally scrolling container has attachments cut off beyond its right edge,
 * so a fade can tell the user there is more to scroll to. The count covers every child of the container, loading
 * placeholders included; each child is observed too, since a thumbnail changes width when its image loads.
 */
export const useHasHiddenAttachments = (containerEl: HTMLElement | null, attachmentCount: number): boolean => {
  const [hasHiddenAttachments, setHasHiddenAttachments] = useState(false);

  useEffect(() => {
    if (!containerEl) {
      return;
    }

    const measure = (): void => {
      const hiddenWidth = containerEl.scrollWidth - containerEl.clientWidth - containerEl.scrollLeft;
      setHasHiddenAttachments(hiddenWidth > HIDDEN_CONTENT_TOLERANCE_PX);
    };

    measure();
    containerEl.addEventListener('scroll', measure);
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(containerEl);
    Array.from(containerEl.children).forEach((attachmentEl) => {
      resizeObserver.observe(attachmentEl);
    });

    return (): void => {
      containerEl.removeEventListener('scroll', measure);
      resizeObserver.disconnect();
    };
  }, [containerEl, attachmentCount]);

  return hasHiddenAttachments;
};
